<template>
    <div data-test-view="user-edit-modal">
        <p data-test-id="user-edit-form-description">
            {{ $t(`usersTeams.manageUsers.formDescriptionExplanation`) }}
        </p>

        <!-- User Info -->
        <l-space :size="2" />

        <div class="tw-grid tw-grid-cols-12 tw-gap-4">
            <div class="tw-col-span-6">
                <l-input-text
                    v-model="user.firstname"
                    :label="$t(`usersTeams.manageUsers.labelFirstname`)"
                    :optional="false"
                    data-test-id="user-edit-form-firstname"
                />
            </div>
            <div class="tw-col-span-6">
                <l-input-text
                    v-model="user.lastname"
                    :label="$t(`usersTeams.manageUsers.labelLastname`)"
                    :optional="false"
                    data-test-id="user-edit-form-lastname"
                />
            </div>
            <div class="tw-col-span-6">
                <l-input-text
                    v-model="user.username"
                    :label="$t(`usersTeams.manageUsers.labelEmail`)"
                    :optional="false"
                    data-test-id="user-edit-form-email"
                />
            </div>
            <div class="tw-col-span-6">
                <l-input-text
                    v-model="user.teamRole.title"
                    :label="$t(`usersTeams.manageUsers.labelTitle`)"
                    :optional="true"
                    data-test-id="user-edit-form-title"
                />
            </div>
            <div class="tw-col-span-6">
                <l-select
                    v-model="user.customRole"
                    :label="$t(`usersTeams.manageUsers.labelLevel`)"
                    :options="[
                        { value: '', text: $t(`usersTeams.manageUsers.labelLevelNone`) },
                        ...levels.map(level => ({ value: level._id, text: level.name })),
                    ]"
                    data-test-id="user-edit-form-level"
                />
            </div>
            <div class="tw-col-span-6">
                <l-input-text
                    v-model="user.location"
                    :label="$t(`usersTeams.manageUsers.labelLocation`)"
                    :optional="true"
                    data-test-id="user-edit-form-location"
                />
            </div>
            <div class="tw-col-span-6">
                <l-date-picker
                    :label="$t(`usersTeams.manageUsers.labelStartDate`)"
                    :placeholder="$t(`usersTeams.manageUsers.placeholderStartDate`)"
                    :value="
                        user.startDate ? this.$moment(user.startDate, 'YYYY-MM-DD').toDate() : ''
                    "
                    :on-date-change="date => (user.startDate = date)"
                    :typeable="false"
                    :is-u-t-c="false"
                    data-test-id="user-edit-form-start-date"
                />
            </div>
            <div class="tw-col-span-6">
                <l-date-picker
                    :label="$t(`usersTeams.manageUsers.labelEndDate`)"
                    :placeholder="$t(`usersTeams.manageUsers.placeholderEndDate`)"
                    :value="user.endDate ? this.$moment(user.endDate, 'YYYY-MM-DD').toDate() : ''"
                    :on-date-change="date => (user.endDate = date)"
                    :is-u-t-c="false"
                    data-test-id="user-edit-form-end-date"
                />
            </div>
            <div class="tw-col-span-6">
                <l-select
                    v-model="user.gender"
                    :label="$t(`usersTeams.manageUsers.labelGender`)"
                    :options="[
                        ...genders.map(gender => ({ value: gender._id, text: gender.label })),
                    ]"
                    data-test-id="user-edit-form-gender"
                />
            </div>
            <div class="tw-col-span-6">
                <l-date-picker
                    :label="$t(`usersTeams.manageUsers.labelBirthday`)"
                    :placeholder="$t(`usersTeams.manageUsers.placeholderBirthday`)"
                    :value="user.birthday ? this.$moment(user.birthday, 'YYYY-MM-DD').toDate() : ''"
                    :on-date-change="date => (user.birthday = date)"
                    :is-u-t-c="false"
                    data-test-id="user-edit-form-birthday"
                />
            </div>

            <!-- custom attributes -->

            <div
                v-for="customAttribute in user.customAttributes"
                :key="customAttribute._id"
                class="tw-col-span-6"
            >
                <l-input-text
                    v-if="customAttribute.type === 'string'"
                    v-model="customAttribute.value"
                    :label="customAttribute.name"
                />
                <l-input-text
                    v-else-if="customAttribute.type === 'number'"
                    v-model="customAttribute.value"
                    type="number"
                    :label="customAttribute.name"
                    optional
                />
                <l-date-picker
                    v-else-if="customAttribute.type === 'date'"
                    v-model="customAttribute.value"
                    :on-date-change="date => (customAttribute.value = date)"
                    :label="customAttribute.name"
                    optional
                />
                <l-select
                    v-else-if="customAttribute.type === 'multipleChoice'"
                    v-model="customAttribute.value"
                    :label="customAttribute.name"
                    :options="
                        customAttribute.options.map(o => {
                            return { key: o.name, value: o.name, text: o.name };
                        })
                    "
                />
                <div v-else class="togglebutton">
                    <label>
                        <input
                            v-model="customAttribute.value"
                            :checked="customAttribute.value"
                            type="checkbox"
                        />
                        <span class="toggle"></span>
                        <span class="color-dark">
                            <span>{{ customAttribute.name }}</span>
                        </span>
                    </label>
                </div>
            </div>

            <!-- end custom attributes -->
        </div>

        <!-- Primary Manager -->
        <l-space :size="2" />

        <div>
            <div
                v-if="user.teamRole.reportsTo && user.teamRole.reportsTo.fullname"
                class="input-group"
            >
                <l-input-text
                    :label="$t(`usersTeams.manageUsers.labelPrimaryManager`)"
                    :disabled="true"
                    :optional="true"
                    :value="user.teamRole.reportsTo && user.teamRole.reportsTo.fullname"
                />
                <span
                    class="input-group-addon color-primary"
                    tabindex="0"
                    @click="removePrimaryManager()"
                    @keyup.enter="removePrimaryManager()"
                >
                    <i class="fa fa-pencil"></i>
                </span>
            </div>
            <div v-else>
                <l-input-typeahead
                    :label="$t(`usersTeams.manageUsers.labelPrimaryManager`)"
                    :data="primaryManagerSearchList"
                    :on-select="typeaheadPrimaryManagerOnSelect"
                    :on-input-change="typeaheadPrimaryManagerOnInputChange"
                    :placeholder="''"
                    data-test-id="user-primary-manager"
                />
            </div>
        </div>
        <!--/Primary Manager -->

        <!-- Additional Managers -->
        <l-space :size="2" />

        <l-input-typeahead
            :label="$t(`usersTeams.manageUsers.labelAdditionalManagers`)"
            :data="additionalManagerSearchList"
            :on-select="typeaheadAdditionalManagersOnSelect"
            :on-input-change="typeaheadAdditionalManagersOnInputChange"
            :placeholder="''"
            data-test-id="user-additional-managers"
        >
        </l-input-typeahead>

        <span
            v-for="(manager, index) in selectedAdditionalManagers"
            v-show="selectedAdditionalManagers.length"
            :key="manager._id"
        >
            <span
                class="label label-default clickable r-default-margin-s"
                tabindex="0"
                data-test-class="team-lead"
                :data-test-index="index"
                :data-test-id="`user-additional-managers-${index}`"
                @click="removeSelectedAdditionalManagers(index)"
                @keyup.enter="removeSelectedAdditionalManagers(index)"
            >
                {{ manager.name }} <i class="fa fa-remove"></i>
            </span>
        </span>
        <!--/Additional Managers -->

        <!-- Team lead of -->
        <div class="tw-mt-10">
            <l-input-typeahead
                :label="$t(`usersTeams.manageUsers.labelAddAsTeamLead`)"
                :placeholder="$t(`usersTeams.placeholderSearchTeam`)"
                :data="teamsFormattedForTypeahead"
                :on-select="onAddAsTeamLead"
                :on-input-change="onTeamsTypeaheadInputChange"
                :remove-margin="true"
                :show-picture="false"
            />

            <div>
                <span
                    v-for="team in form.isTeamLeadFor"
                    :key="team._id"
                    class="label label-light tw-cursor-pointer tw-max-w-full tw-mt-2 tw-mr-2 tw-inline-flex tw-items-center tw-justify-center"
                    tabindex="0"
                    @keyup.enter="onRemoveAsTeamLead(team)"
                    @click="onRemoveAsTeamLead(team)"
                >
                    <i class="fal fa-check tw-mr-2"></i>
                    <span class="tw-line-clamp-1">{{ team.name }}</span>
                    <i class="fal fa-trash tw-ml-2"></i>
                </span>
            </div>
        </div>
        <!-- /Team lead of -->

        <!-- Platform role -->

        <div
            v-if="currentUser.teamRole.role == 'superAdmin' && user._id != currentUser._id"
            class="tw-mt-10"
        >
            <div class="row">
                <div class="col-md-6">
                    <l-select
                        v-model="user.teamRole.role"
                        :label="$t(`usersTeams.manageUsers.labelPlatformRole`)"
                        :options="[
                            ...platformRoles.map(platformRole => ({
                                value: platformRole._id,
                                text: platformRole.label,
                            })),
                        ]"
                        data-test-id="user-edit-form-platform-role"
                    />
                </div>
            </div>

            <l-space :size="2" />

            <l-input-typeahead
                :show-picture="false"
                :show-checkbox="true"
                :label="$t(`usersTeams.manageUsers.labelGroupAccessFor`)"
                :placeholder="$t(`usersTeams.manageUsers.labelGroupAccessSelect`)"
                :data="groupAccessOptions"
                :on-select="item => addGroupAccess(item)"
                :on-input-change="groupAccessInputChange"
                :max-matches="200"
            />
            <span v-for="item in currentGroupAccess" :key="item._id">
                <span class="label label-success r-default-margin-s">
                    <span
                        class="clickable"
                        tabindex="0"
                        @keyup.enter="removeGroupAccess(item)"
                        @click="removeGroupAccess(item)"
                    >
                        <i class="fal fa-check r-default-margin-s"></i>
                        {{ item.name }}
                        <i class="fal fa-trash l-default-margin-s"></i>
                    </span>
                </span>
            </span>
        </div>

        <!-- Attachments -->

        <hr />

        <div class="btn-group btn-block">
            <form name="upload-attachment">
                <label class="btn btn-primary" for="attachment-upload">
                    <span>
                        {{ $t(`usersTeams.manageUsers.labelUploadAttachment`) }}
                    </span>
                    <input
                        id="attachment-upload"
                        type="file"
                        name="file"
                        accept="application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, text/plain, application/pdf, image/*"
                        style="display: none;"
                        @change="e => uploadAttachment(e)"
                    />
                </label>
            </form>
        </div>

        <div v-if="user.attachments && user.attachments.length">
            <div v-for="(attachment, attachmentIndex) in user.attachments" :key="attachment.key">
                <hr />
                <div class="form-group">
                    <l-input-text
                        v-model="attachment.title"
                        :label="$t(`usersTeams.manageUsers.labelAttachmentTitle`)"
                        :optional="false"
                    />
                    <div v-for="accessRole in accessRoles" :key="accessRole">
                        <label
                            v-if="accessRole != 'ADMIN'"
                            :for="`attachment-accessRole-${attachmentIndex}-${accessRole}`"
                        >
                            <input
                                :id="`attachment-accessRole-${attachmentIndex}-${accessRole}`"
                                type="checkbox"
                                :checked="attachment.access.indexOf(accessRole) > -1"
                                @click="toggleAttachmentAccess(attachmentIndex, accessRole)"
                                @keyup.enter="toggleAttachmentAccess(attachmentIndex, accessRole)"
                            />
                            <span class="toggle"></span>
                            <span class="color-dark">{{
                                $t(`usersTeams.manageUsers.labelAttachmentAccess${accessRole}`)
                            }}</span>
                        </label>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3">
                        <button
                            class="btn btn-default"
                            @click="getSingleAttachment(attachment.key)"
                            @keyup.enter="getSingleAttachment(attachment.key)"
                        >
                            <i class="fal fa-download r-default-margin-s"></i>
                            {{ $t("usersTeams.manageUsers.labelOpenAttachment") }}
                        </button>
                    </div>
                    <div class="col-md-9">
                        <button
                            class="btn btn-default pull-right"
                            @click="deleteAttachment(attachmentIndex)"
                            @keyup.enter="deleteAttachment(attachmentIndex)"
                        >
                            <i class="fal fa-trash r-default-margin-s"></i>
                            {{ $t("usersTeams.manageUsers.labelDeleteAttachment") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!--/ Attachments -->

        <hr />

        <div class="row">
            <div class="col-md-12">
                <button
                    class="btn btn-block btn-primary btn-raised"
                    data-test-id="user-edit-form-update"
                    @click="updateUser()"
                >
                    {{ $t(`usersTeams.manageUsers.btnUpdate`) }}
                </button>
            </div>
        </div>

        <!-- Set new password -->
        <div
            v-show="
                user._id !== currentUser._id &&
                    user.status === 'active' &&
                    currentUser.teamRole.role === 'superAdmin'
            "
        >
            <div class="row">
                <div class="col-md-12">
                    <button
                        v-show="!setOneTimePasswordSuccess"
                        class="btn btn-default btn-raised btn-block"
                        data-test-id="user-edit-form-reactivate"
                        @click="setOneTimePasswordWarning = true"
                    >
                        {{ $t(`usersTeams.manageUsers.btnSetOneTimePassword`) }}
                    </button>
                </div>
            </div>

            <l-confirmation-modal
                v-if="setOneTimePasswordWarning"
                :text="$t(`usersTeams.manageUsers.setOneTimePasswordDescription`)"
                @confirm="setOneTimePassword()"
                @cancel="setOneTimePasswordWarning = false"
            />

            <l-text-alert
                v-show="setOneTimePasswordSuccess"
                type="success"
                :text="
                    $t(`usersTeams.manageUsers.setOneTimePasswordSuccess`, {
                        otp: user.oneTimePassword,
                    })
                "
                data-test-id="user-edit-form-reactivate-success"
            />
        </div>
        <!--/Set new password -->

        <!-- Deactivate User -->
        <div
            v-show="
                user._id != currentUser._id &&
                    user.status !== 'deactivated' &&
                    user.teamRole.role !== 'superAdmin'
            "
        >
            <hr />
            <p><l-text :text="$t(`usersTeams.manageUsers.deactivateDescription`)" /></p>
            <div class="row">
                <div class="col-md-12">
                    <button
                        v-show="!deactivateUserSuccess"
                        class="btn btn-block btn-default btn-raised"
                        data-test-id="user-edit-form-deactivate"
                        @click="deactivateUserWarning = true"
                    >
                        {{ $t(`usersTeams.manageUsers.btnDeactivate`) }}
                    </button>
                </div>
            </div>

            <l-confirmation-modal
                v-if="deactivateUserWarning"
                :text="$t(`usersTeams.manageUsers.deactivateDescription`)"
                @confirm="deactivateUser()"
                @cancel="deactivateUserWarning = false"
            />

            <l-text-alert
                v-show="deactivateUserSuccess"
                type="success"
                :text="$t(`usersTeams.manageUsers.deactivateSuccess`)"
                data-test-id="user-edit-form-deactivate-success"
            />
        </div>
        <!--/Deactivate User -->

        <!-- Remove User (only super-admins) -->
        <div
            v-show="
                currentUser.teamRole.role === 'superAdmin' &&
                    user._id != currentUser._id &&
                    user.status === 'deactivated' &&
                    user.teamRole.role !== 'superAdmin'
            "
        >
            <hr />

            <p><l-text :text="$t(`usersTeams.manageUsers.removeDescription`)" /></p>
            <div class="row">
                <div class="col-md-12">
                    <button
                        v-show="!removeUserSuccess"
                        class="btn btn-default btn-block btn-raised"
                        data-test-id="user-edit-form-remove"
                        @click="removeUserWarning = true"
                    >
                        {{ $t(`usersTeams.manageUsers.btnRemove`) }}
                    </button>
                </div>
            </div>

            <l-confirmation-modal
                v-if="removeUserWarning"
                :text="$t(`usersTeams.manageUsers.removeDescription`)"
                @confirm="removeUser()"
                @cancel="removeUserWarning = false"
            />

            <l-text-alert
                v-show="removeUserSuccess"
                type="success"
                :text="$t(`usersTeams.manageUsers.removeSuccess`)"
                data-test-id="user-edit-form-remove-success"
            />
        </div>
        <!--/Remove User -->

        <!-- Reactivate User -->
        <div v-show="user._id !== currentUser._id && user.status === 'deactivated'">
            <hr />
            <p><l-text :text="$t(`usersTeams.manageUsers.reactivateDescription`)" /></p>
            <div class="row">
                <div class="col-md-12">
                    <button
                        v-show="!reactivateUserSuccess"
                        class="btn btn-default btn-raised btn-block"
                        data-test-id="user-edit-form-reactivate"
                        @click="reactivateUserWarning = true"
                    >
                        {{ $t(`usersTeams.manageUsers.btnReactivate`) }}
                    </button>
                </div>
            </div>

            <l-confirmation-modal
                v-if="reactivateUserWarning"
                :text="$t(`usersTeams.manageUsers.reactivateDescription`)"
                @confirm="reactivateUser()"
                @cancel="reactivateUserWarning = false"
            />

            <l-text-alert
                v-show="reactivateUserSuccess"
                type="success"
                :text="$t(`usersTeams.manageUsers.reactivateSuccess`)"
                data-test-id="user-edit-form-reactivate-success"
            />
        </div>
        <!--/Reactivate User -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import formatDate from "@/utils/format-date";
import api from "@/api";

import LDatePicker from "@/components/LDatePicker";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";
import LConfirmationModal from "@/components/LConfirmationModal";

export default {
    components: {
        LDatePicker,
        LInputText,
        LInputTypeahead,
        LSelect,
        LSpace,
        LText,
        LTextAlert,
        LConfirmationModal,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        close: {
            type: Function,
            required: true, // from router-view
        },
    },
    data() {
        return {
            user: null,
            form: {
                isTeamLeadFor: [],
                gotRemovedAsTeamLeadFor: [],
            },
            deactivateUserWarning: false,
            deactivateUserSuccess: false,
            removeUserWarning: false,
            removeUserSuccess: false,
            reactivateUserWarning: false,
            reactivateUserSuccess: false,
            setOneTimePasswordWarning: false,
            setOneTimePasswordSuccess: false,
            newAttachment: "",
            accessRoles: ["ADMIN", "MANAGER", "EMPLOYEE"],
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getUser: "users/details/details",
            getAttachmentUrl: "users/details/attachmentUrl",
            getLevels: "teams/customrole/list",
            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            locationList: "users/location/list",
            currentUser: "users/current/user",
        }),
        teamsFormattedForTypeahead() {
            const existingTeamIds = this.form.isTeamLeadFor.map(team => team._id);

            // we want to filter out non relevant teams for new searches
            return this.teamsSearchList
                .filter(team => !existingTeamIds.includes(team._id))
                .map(team => ({
                    _id: team._id,
                    type: "localTeams",
                    name: team.name,
                }));
        },
        levels() {
            return this.getLevels;
        },
        genders() {
            return [
                {
                    _id: "",
                    label: this.$t(`profile.edit.optionUnset`),
                },
                {
                    _id: "male",
                    label: this.$t(`profile.edit.optionMale`),
                },
                {
                    _id: "female",
                    label: this.$t(`profile.edit.optionFemale`),
                },
                {
                    _id: "diverse",
                    label: this.$t(`profile.edit.optionDiverse`),
                },
            ];
        },
        platformRoles() {
            return [
                {
                    _id: "superAdmin",
                    label: this.$t(`usersTeams.manageUsers.optionSuperAdmin`),
                },
                {
                    _id: "manager",
                    label: this.$t(`usersTeams.manageUsers.optionAdmin`),
                },
                {
                    _id: "member",
                    label: this.$t(`usersTeams.manageUsers.optionUser`),
                },
            ];
        },
        primaryManagerSearchList() {
            return (
                this.usersSearchList
                    // Filter out current user
                    .filter(user => user._id !== this.user._id)
                    // Filter out additional managers
                    .filter(user => !this.user.teamRole?.additionalManagers?.includes(user._id))
                    // Format
                    .map(user => ({
                        _id: user._id,
                        firstname: user.firstname,
                        lastname: user.lastname,
                        name: `${user.firstname} ${user.lastname}`,
                        desc: user.teamRole.title,
                        picture: user.picture,
                    }))
            );
        },
        additionalManagerSearchList() {
            return (
                this.usersSearchList
                    // Filter out current user
                    .filter(user => user._id !== this.user._id)
                    // Filter out primary manager
                    .filter(user => user._id !== this.user.teamRole?.reportsTo?._id)
                    // Format
                    .map(user => ({
                        _id: user._id,
                        firstname: user.firstname,
                        lastname: user.lastname,
                        name: `${user.firstname} ${user.lastname}`,
                        desc: user.teamRole.title,
                        picture: user.picture,
                    }))
            );
        },
        selectedAdditionalManagers() {
            return (this.user.teamRole?.additionalManagers || []).map(user => ({
                ...user,
                name: `${user.firstname} ${user.lastname}`,
            }));
        },
        groupAccessOptions() {
            const existingUsers = this.user.groupAccess?.users ?? [];
            const existingLocalTeams = this.user.groupAccess?.localTeams ?? [];
            const existingLocations = this.user.groupAccess?.locations ?? [];

            return [
                ...this.usersSearchList
                    .filter(user => !existingUsers.map(p => p._id).includes(user._id))
                    .map(user => ({
                        _id: user._id,
                        type: "users",
                        name: user.fullname,
                    })),
                ...this.teamsSearchList
                    .filter(team => !existingLocalTeams.map(t => t._id).includes(team._id))
                    .map(team => ({
                        _id: team._id,
                        type: "localTeams",
                        name: team.name,
                    })),
                ...(this.locationList || [])
                    .filter(l => !existingLocations.includes(l.location))
                    .map(l => ({
                        _id: l._id,
                        type: "locations",
                        name: l.name,
                    })),
            ];
        },
        currentGroupAccess() {
            return [
                ...(this.user.groupAccess?.users ?? []).map((item, index) => {
                    return {
                        _id: item._id,
                        name: item.name || item.fullname,
                        type: "users",
                        index,
                    };
                }),
                ...(this.user.groupAccess?.localTeams ?? []).map((item, index) => {
                    return {
                        _id: item._id,
                        name: item.name,
                        type: "localTeams",
                        index,
                    };
                }),
                ...(this.user.groupAccess?.locations ?? []).map((item, index) => {
                    return {
                        _id: item._id ? item._id : item,
                        name: item.name ? item.name : item,
                        type: "locations",
                        index,
                    };
                }),
            ];
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.user = this.getUser(this.id);
                this.form.isTeamLeadFor = [...(this.user.isTeamLeadFor ?? [])];
            },
        },
    },
    methods: {
        addGroupAccess(item) {
            if (!item) {
                return;
            }

            if (!this.user.groupAccess) {
                this.$set(this.user, "groupAccess", {});
            }

            if (!this.user.groupAccess[item.type]) {
                this.$set(this.user.groupAccess, item.type, []);
            }

            this.$set(
                this.user.groupAccess[item.type],
                this.user.groupAccess[item.type].length,
                item,
            );
        },
        removeGroupAccess(item) {
            this.user.groupAccess[item.type].splice(item.index, 1);
        },
        onTeamsTypeaheadInputChange(query = "") {
            if (query.length >= 2) {
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("teams/search/reset");
            }
        },
        onAddAsTeamLead(team) {
            // pre-caution to prevent duplicate addition
            const isTeamLeadAlready = this.form.isTeamLeadFor.find(
                existingTeam => existingTeam._id === team._id,
            );
            if (isTeamLeadAlready) return;

            this.form.isTeamLeadFor.push(team);
        },
        onRemoveAsTeamLead(team) {
            const indexOfTeamToRemove = this.form.isTeamLeadFor.findIndex(
                existingTeam => existingTeam._id === team._id,
            );
            if (indexOfTeamToRemove > -1) this.form.isTeamLeadFor.splice(indexOfTeamToRemove, 1);

            this.form.gotRemovedAsTeamLeadFor.push(team);
        },
        groupAccessInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        typeaheadPrimaryManagerOnSelect(user) {
            this.user.teamRole.reportsTo = {
                _id: user._id,
                fullname: user.name,
            };
        },
        typeaheadPrimaryManagerOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        typeaheadAdditionalManagersOnSelect(user) {
            this.selectedAdditionalManagers.push(user);
            this.$store.dispatch("users/search/reset");
        },
        typeaheadAdditionalManagersOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },

        removePrimaryManager() {
            this.user.teamRole.reportsTo = null;
        },
        removeSelectedAdditionalManagers(index) {
            this.selectedAdditionalManagers.splice(index, 1);
            this.$forceUpdate();
        },

        updateUser() {
            const user = {
                ...this.user,
                ...this.form,
                teamRole: {
                    ...this.user.teamRole,
                    additionalManagers: this.selectedAdditionalManagers || [],
                },
                startDate:
                    this.user.startDate && formatDate(new Date(this.user.startDate), "DD-MM-YYYY"),
                endDate: this.user.endDate && formatDate(new Date(this.user.endDate), "DD-MM-YYYY"),
                birthday:
                    this.user.birthday && formatDate(new Date(this.user.birthday), "DD-MM-YYYY"),
            };

            const scope = [
                "name",
                "title",
                "customRole",
                "startDate",
                "endDate",
                "username",
                "location",
                "birthday",
                "gender",
                "rights",
                "manager",
                "additionalManagers",
                "attachments",
                "role",
                "groupAccess",
                "customAttributes",
                "teamLead",
            ];

            this.$store
                .dispatch({ type: "users/details/update", payload: { user, scope } })
                .then(this.close);
        },
        uploadAttachment(e) {
            this.error = false;

            const { files } = e.target;

            if (!files) {
                return;
            }

            const file = files[0];

            const formData = new FormData();
            formData.append("file", file);

            api.post("/api/uploads/attachment", formData)
                .then(res => {
                    this.newAttachment = "";

                    if (!this.user.attachments) {
                        this.user.attachments = [];
                    }

                    if (!res.data) {
                        return;
                    }

                    const fileInput = document.querySelector("input[type=file]");
                    const path = fileInput.value;
                    const fileName = path.split(/(\\|\/)/g).pop();

                    this.user.attachments.push({
                        key: res?.data?.key,
                        title: fileName,
                        access: ["ADMIN", "MANAGER", "EMPLOYEE"],
                    });

                    this.$forceUpdate();
                })
                .catch(() => {
                    this.error = true;
                });
        },
        toggleAttachmentAccess(index, accessRole) {
            var accessIndex = this.user.attachments[index]?.access?.indexOf(accessRole);

            if (accessIndex > -1) {
                this.user.attachments[index].access.splice(accessIndex, 1);
            } else {
                this.user.attachments[index].access.push(accessRole);
            }
        },
        deleteAttachment(index) {
            this.user.attachments.splice(index, 1);
        },
        getSingleAttachment(key) {
            this.$store
                .dispatch({ type: "users/details/getSingleAttachment", payload: { key } })
                .then(() => {
                    window.open(this.getAttachmentUrl, "_blank", "noopener noreferrer");
                });
        },
        deactivateUser() {
            this.deactivateUserWarning = false;

            this.$store
                .dispatch({ type: "users/details/deactivate", payload: { id: this.user._id } })
                .then(() => {
                    this.deactivateUserSuccess = true;
                });
        },
        reactivateUser() {
            this.reactivateUserWarning = false;

            this.$store
                .dispatch({ type: "users/details/reactivate", payload: { id: this.user._id } })
                .then(() => {
                    this.reactivateUserSuccess = true;
                });
        },
        removeUser() {
            this.removeUserWarning = false;

            this.$store
                .dispatch({ type: "users/details/remove", payload: { id: this.user._id } })
                .then(() => {
                    this.removeUserSuccess = true;
                });
        },
        setOneTimePassword() {
            this.setOneTimePasswordWarning = false;

            this.$store
                .dispatch({
                    type: "users/details/setOneTimePassword",
                    payload: { id: this.user._id },
                })
                .then(() => {
                    this.setOneTimePasswordSuccess = true;
                    this.user = this.getUser(this.user._id);
                });
        },
    },
};
</script>
